import { APPLICATION_PERIOD_TERM_INDEX, parseApplicationPeriodFromTerm } from './applicationPeriod';
import { parseChallengeCardBackground, parseChallengeCardSubtitle, parseChallengeCardTitle } from './challengeCard';
import { createDefaultState, withDefaultSuccessSteps } from './defaults';
import { createRowId } from '../ids';
import {
  asNumber,
  asString,
  nonEmptyOrDefault,
  parseObject,
  parseOptionalArrayField,
  parseOptionalObjectField,
  parseOptionalStringArrayField,
  parseJsonObject,
} from '../parseHelpers';
import { defaultLogoImageId, resolveProgramDisplayName } from './programs';
import { normalizeCampaignSlug } from './slug';
import type {
  CampaignMode,
  ConditionRow,
  FaqRow,
  StatusCampaignPayloadBuilderState,
  ProgramConfig,
  SuccessStepRow,
  TextStepRow,
  TierMappingRow,
} from './types';

function parseTierMappings(program: Record<string, unknown>, programLabel: string): Array<TierMappingRow> {
  return parseOptionalArrayField(program, 'tiers')
    .map((tier, index) => {
      const row = parseObject(tier, `${programLabel} tier ${index + 1}`);
      const sourceTier = asString(row.sourceTier);
      const targetSocieteTier = asString(row.targetSocieteTier);
      if (!sourceTier && !targetSocieteTier) return null;
      return {
        id: createRowId(),
        sourceTier,
        targetSocieteTier,
      };
    })
    .filter((row): row is TierMappingRow => row !== null);
}

function parsePrograms(payload: Record<string, unknown>): Array<ProgramConfig> {
  return parseOptionalArrayField(payload, 'programs')
    .map((program, index) => {
      const label = `Program ${index + 1}`;
      const row = parseObject(program, label);
      const rawName = asString(row.name);
      if (!rawName) return null;
      const programName = resolveProgramDisplayName(rawName);
      return {
        id: createRowId(),
        programName,
        logoImageId: asString(row.logoImageId, defaultLogoImageId(programName)),
        tierMappings: parseTierMappings(row, label),
      };
    })
    .filter((program): program is ProgramConfig => program !== null);
}

function parseTextSteps(payload: Record<string, unknown>, key: string, rowLabel: string): Array<TextStepRow> {
  return parseOptionalArrayField(payload, key)
    .map((step, index) => {
      const row = parseObject(step, `${rowLabel} ${index + 1}`);
      return {
        id: createRowId(),
        title: asI18nFallbackString(row, 'title'),
        description: asI18nFallbackString(row, 'description'),
      };
    })
    .filter((step): step is TextStepRow => step !== null);
}

function mergeSuccessSteps(parsed: Array<SuccessStepRow>): Array<SuccessStepRow> {
  const defaults = withDefaultSuccessSteps(createDefaultState('statusMatch')).successSteps;
  return defaults.map((defaultStep) => {
    const existing = parsed.find((step) => step.key === defaultStep.key);
    if (!existing) return defaultStep;
    return {
      ...defaultStep,
      title: existing.title || defaultStep.title,
      description: existing.description || defaultStep.description,
    };
  });
}

function parseConditions(payload: Record<string, unknown>): Array<ConditionRow> {
  return parseOptionalArrayField(payload, 'conditions')
    .map((condition, index) => {
      const row = parseObject(condition, `Condition ${index + 1}`);
      return {
        id: createRowId(),
        icon: asString(row.icon, 'clock'),
        title: asI18nFallbackString(row, 'title'),
        description: asI18nFallbackString(row, 'description'),
      };
    })
    .filter((condition): condition is ConditionRow => condition !== null);
}

function asI18nFallbackString(source: Record<string, unknown>, key: string, fallback = ''): string {
  return asString(source[key], asString(source[`${key}-i18n`], fallback));
}

function parseOptionalI18nStringArrayField(source: Record<string, unknown>, key: string, label = key): Array<string> {
  return key in source
    ? parseOptionalStringArrayField(source, key, label)
    : parseOptionalStringArrayField(source, `${key}-i18n`, `${label}-i18n`);
}

function parseFaqs(payload: Record<string, unknown>): Array<FaqRow> {
  return parseOptionalArrayField(payload, 'faqs')
    .map((faq, index) => {
      const row = parseObject(faq, `FAQ ${index + 1}`);
      return {
        id: createRowId(),
        question: asI18nFallbackString(row, 'question'),
        answer: asI18nFallbackString(row, 'answer'),
      };
    })
    .filter((faq): faq is FaqRow => faq !== null);
}

export function parsePayloadJson(raw: string, mode: CampaignMode): StatusCampaignPayloadBuilderState {
  const payload = parseJsonObject(raw);
  const defaults = createDefaultState(mode);

  if (mode === 'statusClaim') {
    const hero = parseOptionalObjectField(payload, 'hero');
    const partner = parseOptionalObjectField(payload, 'partner');
    const secondary = parseOptionalObjectField(payload, 'secondarySection');

    return {
      ...defaults,
      mode,
      campaignId: asString(payload.campaignId),
      slug: normalizeCampaignSlug(asString(payload.slug)),
      termsSlug: asString(payload.termsSlug, defaults.termsSlug),
      heroTitle: asI18nFallbackString(hero, 'title', defaults.heroTitle),
      heroSubtitle: asI18nFallbackString(hero, 'subtitle'),
      heroImageId: asString(hero.imageId, defaults.heroImageId),
      partnerName: asString(partner.name),
      partnerLogoImageId: asString(partner.logoImageId),
      tiersGranted: parseOptionalStringArrayField(payload, 'tiers'),
      secondaryTitle: asI18nFallbackString(secondary, 'title', defaults.secondaryTitle),
      secondarySubtitle: asI18nFallbackString(secondary, 'subtitle', defaults.secondarySubtitle),
    };
  }

  const hero = parseOptionalObjectField(payload, 'hero');
  const evidence = parseOptionalObjectField(payload, 'evidence');
  const challengeCard = parseOptionalObjectField(payload, 'challengeCard');
  const cardTitle = asI18nFallbackString(challengeCard, 'cardTitle');
  const subtitleParts = parseChallengeCardSubtitle(asI18nFallbackString(challengeCard, 'cardSubtitle'));

  const parsedSuccessSteps = 'successSteps' in payload
    ? mergeSuccessSteps(
        parseOptionalArrayField(payload, 'successSteps')
          .map((step, index) => {
            const row = parseObject(step, `Success step ${index + 1}`);
            return {
              id: createRowId(),
              key: asString(row.key),
              title: asI18nFallbackString(row, 'title'),
              description: asI18nFallbackString(row, 'description'),
            };
          })
          .filter((step): step is SuccessStepRow => step !== null && Boolean(step.key)),
      )
    : defaults.successSteps;

  const terms = nonEmptyOrDefault(parseOptionalI18nStringArrayField(payload, 'terms'), [...defaults.terms]);
  const applicationPeriod = parseApplicationPeriodFromTerm(terms[APPLICATION_PERIOD_TERM_INDEX] ?? '') ?? {
    from: defaults.applicationPeriodFrom,
    to: defaults.applicationPeriodTo,
  };

  return withDefaultSuccessSteps({
    ...defaults,
    mode,
    campaignId: asString(payload.campaignId),
    slug: normalizeCampaignSlug(asString(payload.slug)),
    termsSlug: asString(payload.termsSlug, defaults.termsSlug),
    heroTitle: asI18nFallbackString(hero, 'title', defaults.heroTitle),
    heroSubtitle: asI18nFallbackString(hero, 'subtitle', defaults.heroSubtitle),
    heroDescription: asI18nFallbackString(hero, 'description', defaults.heroDescription),
    heroCtaText: asI18nFallbackString(hero, 'ctaText', defaults.heroCtaText),
    heroImageId: asString(hero.imageId, defaults.heroImageId),
    allowedRegions: 'allowedRegions' in payload ? parseOptionalStringArrayField(payload, 'allowedRegions') : undefined,
    programs: parsePrograms(payload),
    conditions: parseConditions(payload),
    howItWorks: nonEmptyOrDefault(parseTextSteps(payload, 'howItWorks', 'How It Works step'), defaults.howItWorks),
    terms,
    applicationPeriodFrom: applicationPeriod.from,
    applicationPeriodTo: applicationPeriod.to,
    faqs: nonEmptyOrDefault(parseFaqs(payload), defaults.faqs),
    challengeCardTitleSuffix: cardTitle ? parseChallengeCardTitle(cardTitle) : defaults.challengeCardTitleSuffix,
    challengeCardSubtitleBefore: subtitleParts.before || defaults.challengeCardSubtitleBefore,
    challengeCardSubtitleAfter: subtitleParts.after || defaults.challengeCardSubtitleAfter,
    challengeCardBackground: parseChallengeCardBackground(challengeCard.background),
    evidenceMinPhotos: asNumber(evidence.minPhotos, defaults.evidenceMinPhotos),
    evidenceVerificationImageId: asString(evidence.verificationImageId, defaults.evidenceVerificationImageId),
    evidencePhotoHints: nonEmptyOrDefault(
      parseOptionalI18nStringArrayField(evidence, 'photoHints', 'evidence.photoHints'),
      defaults.evidencePhotoHints,
    ),
    evidenceAcceptedFormats: nonEmptyOrDefault(
      parseOptionalStringArrayField(evidence, 'acceptedFormats', 'evidence.acceptedFormats'),
      defaults.evidenceAcceptedFormats,
    ),
    evidenceProofRequirements: nonEmptyOrDefault(
      parseOptionalI18nStringArrayField(evidence, 'proofRequirements', 'evidence.proofRequirements'),
      defaults.evidenceProofRequirements,
    ),
    successSteps: parsedSuccessSteps,
  });
}
